import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { auth, firestore } from '../firebase';
import { signOut } from 'firebase/auth';
import {
  collection, query, where, onSnapshot,
  addDoc, serverTimestamp, orderBy,
} from 'firebase/firestore';

// ── Constantes ───────────────────────────────────────────────────────────────
const INVOICE_STATUS = {
  payé:       { label: 'Payé',       color: '#4ade80', bg: 'rgba(74,222,128,0.08)'  },
  en_attente: { label: 'En attente', color: '#fbbf24', bg: 'rgba(251,191,36,0.08)'  },
  en_retard:  { label: 'En retard',  color: '#f87171', bg: 'rgba(248,113,113,0.08)' },
};

const WEB_STATUS = {
  en_dev:      { label: 'En développement', color: '#C8FF00', dot: '#C8FF00'   },
  en_test:     { label: 'Prêt pour révision', color: '#60a5fa', dot: '#60a5fa'  },
  en_ligne:    { label: 'En ligne',          color: '#4ade80', dot: '#4ade80'  },
  maintenance: { label: 'Maintenance',       color: '#a78bfa', dot: '#a78bfa' },
  pause:       { label: 'En pause',          color: '#555',    dot: '#444' },
};

const PROJ_STATUS = {
  active:    { label: 'En cours',    color: '#C8FF00', bg: 'rgba(200,255,0,0.08)' },
  completed: { label: 'Livré ✓',    color: '#4ade80', bg: 'rgba(74,222,128,0.08)' },
  pending:   { label: 'En attente', color: '#fbbf24', bg: 'rgba(251,191,36,0.08)' },
  paused:    { label: 'En pause',   color: '#555',    bg: 'rgba(255,255,255,0.04)' },
};

// ── Micro-composants ─────────────────────────────────────────────────────────
const Badge = ({ label, color, bg }) => (
  <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:99, fontSize:'0.67rem', fontWeight:700, color, background: bg || `${color}15`, textTransform:'uppercase', letterSpacing:'0.05em' }}>
    {label}
  </span>
);

const Bar = ({ value = 0, color = '#C8FF00' }) => (
  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
    <div style={{ flex:1, height:4, background:'rgba(255,255,255,0.06)', borderRadius:99, overflow:'hidden' }}>
      <div style={{ height:'100%', width:`${Math.min(value,100)}%`, background:color, borderRadius:99, transition:'width 1s ease' }}/>
    </div>
    <span style={{ fontSize:'0.68rem', color:'#444', minWidth:28, textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{value}%</span>
  </div>
);

const Divider = () => <div style={{ height:1, background:'rgba(255,255,255,0.04)', margin:'32px 0' }}/>;

const SectionLabel = ({ children }) => (
  <div style={{ fontSize:'0.62rem', color:'#333', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
    <span style={{ width:16, height:1, background:'#333', display:'inline-block' }}/>
    {children}
  </div>
);

// ── Approbation d'un site ────────────────────────────────────────────────────
function ApprovalPanel({ website, clientEmail, clientName }) {
  const [approvals, setApprovals] = useState([]);
  const [showForm, setShowForm]   = useState(false);
  const [comment,  setComment]    = useState('');
  const [sending,  setSending]    = useState(false);
  const [sent,     setSent]       = useState(null);

  useEffect(() => {
    if (!website.id) return;
    const q = query(
      collection(firestore, 'approvals'),
      where('websiteId', '==', website.id),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, s => setApprovals(s.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [website.id]);

  const lastApproval = approvals[0] || null;
  const canApprove   = website.status === 'en_test';

  const submit = async (action) => {
    setSending(true);
    try {
      await addDoc(collection(firestore, 'approvals'), {
        websiteId:   website.id,
        websiteName: website.name,
        clientEmail, clientName,
        action,
        comment: comment.trim(),
        createdAt: serverTimestamp(),
      });
      setSent(action);
      setShowForm(false);
      setComment('');
    } catch {}
    setSending(false);
  };

  if (!canApprove && !lastApproval) return null;

  return (
    <div style={{ marginTop:16, borderTop:'1px solid rgba(255,255,255,0.05)', paddingTop:14 }}>
      {/* Dernier statut d'approbation */}
      {lastApproval && (
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom: canApprove ? 12 : 0 }}>
          {lastApproval.action === 'approved'
            ? <span style={{ fontSize:'0.72rem', color:'#4ade80', fontWeight:700 }}>✓ Approuvé par vous</span>
            : <span style={{ fontSize:'0.72rem', color:'#fbbf24', fontWeight:700 }}>✎ Révision demandée</span>
          }
          {lastApproval.comment && (
            <span style={{ fontSize:'0.7rem', color:'#333', fontStyle:'italic' }}>— "{lastApproval.comment.slice(0,60)}{lastApproval.comment.length>60?'…':''}"</span>
          )}
        </div>
      )}

      {/* Boutons d'action */}
      {canApprove && !sent && (
        <>
          {!showForm ? (
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button onClick={() => submit('approved')} disabled={sending}
                style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(74,222,128,0.12)', color:'#4ade80', border:'1px solid rgba(74,222,128,0.25)', padding:'7px 14px', borderRadius:8, fontWeight:700, fontSize:'0.75rem', cursor:'pointer' }}>
                ✓ J'approuve
              </button>
              <button onClick={() => setShowForm(true)}
                style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(251,191,36,0.1)', color:'#fbbf24', border:'1px solid rgba(251,191,36,0.2)', padding:'7px 14px', borderRadius:8, fontWeight:700, fontSize:'0.75rem', cursor:'pointer' }}>
                ✎ Demande de modification
              </button>
            </div>
          ) : (
            <div>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
                placeholder="Décrivez les modifications souhaitées…"
                style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'10px 12px', color:'#EAEAEA', fontSize:'0.82rem', outline:'none', resize:'vertical', boxSizing:'border-box', marginBottom:8, fontFamily:'inherit' }}/>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => submit('revision')} disabled={sending || !comment.trim()}
                  style={{ background:'#fbbf24', color:'#000', border:'none', padding:'8px 16px', borderRadius:7, fontWeight:700, fontSize:'0.78rem', cursor: comment.trim() ? 'pointer' : 'not-allowed', opacity: comment.trim() ? 1 : 0.5 }}>
                  Envoyer
                </button>
                <button onClick={() => { setShowForm(false); setComment(''); }}
                  style={{ background:'none', border:'1px solid rgba(255,255,255,0.08)', color:'#555', padding:'8px 14px', borderRadius:7, fontSize:'0.78rem', cursor:'pointer' }}>
                  Annuler
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {sent && (
        <div style={{ fontSize:'0.78rem', color: sent==='approved' ? '#4ade80' : '#fbbf24', fontWeight:700 }}>
          {sent === 'approved' ? '✓ Approbation envoyée !' : '✎ Demande envoyée à l\'équipe.'}
        </div>
      )}
    </div>
  );
}

// ── Portail principal ────────────────────────────────────────────────────────
export default function ClientPortal() {
  const { currentUser, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  const [clientRecord, setClientRecord] = useState(null);
  const [websites,  setWebsites]  = useState([]);
  const [projects,  setProjects]  = useState([]);
  const [invoices,  setInvoices]  = useState([]);
  const [fetching,  setFetching]  = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [msgForm,   setMsgForm]   = useState({ subject:'', body:'' });
  const [msgSent,   setMsgSent]   = useState(false);
  const [sending,   setSending]   = useState(false);

  useEffect(() => {
    if (!loading && !currentUser) navigate('/auth');
  }, [loading, currentUser, navigate]);

  useEffect(() => {
    if (!currentUser) return;
    const clientQ = query(collection(firestore, 'clients'), where('email', '==', currentUser.email));
    const unsubClient = onSnapshot(clientQ, snap => {
      if (snap.empty) { setFetching(false); return; }
      const rec = { id: snap.docs[0].id, ...snap.docs[0].data() };
      setClientRecord(rec);

      const webQ  = query(collection(firestore, 'websites'), where('client', '==', rec.name));
      onSnapshot(webQ,  s => setWebsites(s.docs.map(d => ({ id:d.id, ...d.data() }))));

      const projQ = query(collection(firestore, 'projects'), where('client', '==', rec.name));
      onSnapshot(projQ, s => setProjects(s.docs.map(d => ({ id:d.id, ...d.data() }))));

      const invQ  = query(collection(firestore, 'invoices'), where('clientEmail', '==', currentUser.email));
      onSnapshot(invQ,  s => { setInvoices(s.docs.map(d => ({ id:d.id, ...d.data() }))); setFetching(false); });
    });
    return () => unsubClient();
  }, [currentUser]);

  const sendMessage = async () => {
    if (!msgForm.subject || !msgForm.body || sending) return;
    setSending(true);
    try {
      await addDoc(collection(firestore, 'messages'), {
        from: clientRecord?.name || currentUser.email,
        email: currentUser.email,
        subject: msgForm.subject,
        body: msgForm.body,
        read: false,
        createdAt: serverTimestamp(),
      });
      setMsgSent(true);
      setMsgForm({ subject:'', body:'' });
    } catch {}
    setSending(false);
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading || fetching) return (
    <div style={{ background:'#060608', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width:32, height:32, border:'2px solid rgba(200,255,0,0.15)', borderTop:'2px solid #C8FF00', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
      <span style={{ color:'#333', fontSize:'0.72rem', letterSpacing:'0.1em', textTransform:'uppercase' }}>Chargement</span>
    </div>
  );

  if (!clientRecord) return (
    <div style={{ background:'#060608', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16, padding:24 }}>
      <div style={{ fontSize:'2.5rem' }}>🔒</div>
      <h2 style={{ color:'#EAEAEA', fontWeight:800, fontSize:'1.2rem' }}>Accès non configuré</h2>
      <p style={{ color:'#444', fontSize:'0.88rem', textAlign:'center', maxWidth:340 }}>Votre compte n'est pas encore lié à un dossier client. Contactez l'agence pour activer votre accès.</p>
      <a href="mailto:info@ucreative.ca" style={{ color:'#C8FF00', fontSize:'0.85rem', fontWeight:700 }}>Contacter l'agence</a>
    </div>
  );

  // ── Computed ───────────────────────────────────────────────────────────────
  const firstName     = (clientRecord.name || '').split(' ')[0] || currentUser.email.split('@')[0];
  const totalFacturé  = invoices.reduce((a,i) => a + (i.amount||0), 0);
  const totalPayé     = invoices.filter(i=>i.status==='payé').reduce((a,i) => a + (i.amount||0), 0);
  const totalAttente  = invoices.filter(i=>i.status==='en_attente').reduce((a,i) => a + (i.amount||0), 0);
  const awaitingReview = websites.filter(w => w.status === 'en_test').length;
  const avgProgress   = projects.length
    ? Math.round(projects.reduce((a,p) => a+(p.progress||0), 0) / projects.length)
    : 0;

  const TABS = [
    { id:'overview',  label:'Vue d\'ensemble' },
    { id:'sites',     label:`Sites${websites.length ? ` (${websites.length})` : ''}` },
    { id:'projects',  label:`Projets${projects.length ? ` (${projects.length})` : ''}` },
    { id:'invoices',  label:`Factures${invoices.length ? ` (${invoices.length})` : ''}` },
    { id:'messages',  label:'Contact' },
  ];

  const inp = { width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'10px 12px', color:'#EAEAEA', fontSize:'0.88rem', outline:'none', boxSizing:'border-box', fontFamily:'inherit' };

  return (
    <div style={{ background:'#060608', minHeight:'100vh', fontFamily:'"Inter",-apple-system,sans-serif', color:'#EAEAEA' }}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:99px}
        input:focus,textarea:focus{border-color:rgba(200,255,0,0.3)!important}
      `}</style>

      {/* ── Header ── */}
      <header style={{ borderBottom:'1px solid rgba(255,255,255,0.05)', padding:'0 32px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'rgba(6,6,8,0.96)', backdropFilter:'blur(16px)', zIndex:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:26, height:26, background:'#C8FF00', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, color:'#000', fontSize:'0.78rem' }}>U</div>
          <span style={{ fontWeight:900, fontSize:'0.92rem', letterSpacing:'-0.03em' }}>creative</span>
          <span style={{ color:'#2a2a2a', fontSize:'0.8rem', marginLeft:2 }}>/ Espace client</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          {awaitingReview > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:99, padding:'5px 12px' }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:'#60a5fa', display:'inline-block', animation:'pulse 2s infinite' }}/>
              <span style={{ color:'#60a5fa', fontSize:'0.72rem', fontWeight:700 }}>{awaitingReview} site{awaitingReview>1?'s':''} à réviser</span>
            </div>
          )}
          <span style={{ color:'#333', fontSize:'0.78rem' }}>{currentUser.email}</span>
          <button onClick={async()=>{ await signOut(auth); navigate('/'); }}
            style={{ background:'transparent', border:'1px solid rgba(255,255,255,0.07)', color:'#444', padding:'5px 12px', borderRadius:7, cursor:'pointer', fontSize:'0.75rem', fontWeight:600, transition:'all 0.15s' }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,0.15)';e.currentTarget.style.color='#888'}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,0.07)';e.currentTarget.style.color='#444'}}>
            Déconnexion
          </button>
        </div>
      </header>

      <div style={{ maxWidth:960, margin:'0 auto', padding:'40px 24px 80px' }}>

        {/* ── Welcome ── */}
        <div style={{ marginBottom:36 }}>
          <div style={{ fontSize:'0.62rem', color:'#333', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:8 }}>Bonjour</div>
          <h1 style={{ fontSize:'2.2rem', fontWeight:900, letterSpacing:'-0.05em', marginBottom:4 }}>
            {firstName} <span style={{ background:'linear-gradient(135deg,#C8FF00,#94CC00)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>👋</span>
          </h1>
          <p style={{ color:'#444', fontSize:'0.88rem' }}>Voici l'état de vos projets en temps réel.</p>
        </div>

        {/* ── KPI cards ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:36 }}>
          {[
            { label:'Sites web',       value: websites.length  || '—', color:'#C8FF00',  sub: websites.filter(w=>w.status==='en_ligne').length + ' en ligne' },
            { label:'Projets',         value: projects.length  || '—', color:'#60a5fa',  sub: `${avgProgress}% avancement moy.` },
            { label:'Factures payées', value: `$${totalPayé.toLocaleString()}`, color:'#4ade80', sub: `sur $${totalFacturé.toLocaleString()}` },
            { label:'En attente',      value: `$${totalAttente.toLocaleString()}`, color:'#fbbf24', sub: `${invoices.filter(i=>i.status==='en_attente').length} facture(s)` },
          ].map(k => (
            <div key={k.label} style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:'16px 18px' }}>
              <div style={{ fontSize:'0.6rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:10 }}>{k.label}</div>
              <div style={{ fontSize:'1.7rem', fontWeight:900, color:k.color, letterSpacing:'-0.04em', lineHeight:1, marginBottom:4, fontVariantNumeric:'tabular-nums' }}>{k.value}</div>
              <div style={{ fontSize:'0.68rem', color:'#333' }}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Alerte révision ── */}
        {awaitingReview > 0 && (
          <div style={{ background:'rgba(96,165,250,0.07)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:12, padding:'16px 20px', marginBottom:28, display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ fontSize:'1.4rem' }}>👁</div>
            <div>
              <div style={{ fontWeight:800, color:'#60a5fa', fontSize:'0.9rem', marginBottom:2 }}>
                {awaitingReview} site{awaitingReview>1?'s':''} en attente de votre approbation
              </div>
              <div style={{ color:'#446', fontSize:'0.8rem', color:'rgba(96,165,250,0.5)' }}>
                Consultez l'onglet Sites pour approuver ou demander des modifications.
              </div>
            </div>
            <button onClick={() => setActiveTab('sites')}
              style={{ marginLeft:'auto', background:'rgba(96,165,250,0.15)', border:'1px solid rgba(96,165,250,0.3)', borderRadius:8, color:'#60a5fa', padding:'7px 14px', fontWeight:700, fontSize:'0.78rem', cursor:'pointer', whiteSpace:'nowrap' }}>
              Voir →
            </button>
          </div>
        )}

        {/* ── Tabs ── */}
        <div style={{ display:'flex', gap:0, borderBottom:'1px solid rgba(255,255,255,0.05)', marginBottom:28 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ background:'none', border:'none', borderBottom: activeTab===t.id ? '2px solid #C8FF00' : '2px solid transparent', color: activeTab===t.id ? '#EAEAEA' : '#444', padding:'10px 16px 9px', cursor:'pointer', fontSize:'0.8rem', fontWeight: activeTab===t.id ? 700 : 500, transition:'all 0.15s', whiteSpace:'nowrap' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ══ OVERVIEW ════════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div>
            {/* Sites récents */}
            {websites.length > 0 && (
              <>
                <SectionLabel>Vos sites web</SectionLabel>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12, marginBottom:32 }}>
                  {websites.slice(0,4).map(w => {
                    const ws = WEB_STATUS[w.status] || WEB_STATUS.pause;
                    return (
                      <div key={w.id} style={{ background:'#0d0d10', border:`1px solid ${w.status==='en_test' ? 'rgba(96,165,250,0.2)' : 'rgba(255,255,255,0.05)'}`, borderRadius:12, padding:18 }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ width:6, height:6, borderRadius:'50%', background:ws.dot, boxShadow:`0 0 6px ${ws.dot}`, display:'inline-block' }}/>
                            <span style={{ fontSize:'0.66rem', color:ws.color, fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase' }}>{ws.label}</span>
                          </div>
                          {w.status === 'en_test' && <span style={{ fontSize:'0.65rem', color:'#60a5fa', fontWeight:700, background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.2)', padding:'2px 8px', borderRadius:99 }}>À réviser</span>}
                        </div>
                        <div style={{ fontWeight:800, fontSize:'0.95rem', marginBottom:4 }}>{w.name}</div>
                        {w.url && <a href={w.url.startsWith('http')?w.url:`https://${w.url}`} target="_blank" rel="noreferrer" style={{ color:'#60a5fa', fontSize:'0.72rem', display:'block', marginBottom:10 }}>{w.url.replace(/^https?:\/\//, '')}</a>}
                        <Bar value={w.progress||0} color={w.status==='en_ligne'?'#4ade80':'#C8FF00'}/>
                        <ApprovalPanel website={w} clientEmail={currentUser.email} clientName={clientRecord.name}/>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Projets récents */}
            {projects.length > 0 && (
              <>
                <SectionLabel>Projets en cours</SectionLabel>
                <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, overflow:'hidden', marginBottom:32 }}>
                  {projects.slice(0,5).map((p, i) => {
                    const ps = PROJ_STATUS[p.status] || PROJ_STATUS.pending;
                    return (
                      <div key={p.id} style={{ padding:'14px 20px', borderBottom: i<Math.min(projects.length,5)-1 ? '1px solid rgba(255,255,255,0.04)' : 'none', display:'grid', gridTemplateColumns:'1fr 180px 110px', alignItems:'center', gap:20 }}>
                        <span style={{ fontWeight:700, fontSize:'0.88rem' }}>{p.name}</span>
                        <Bar value={p.progress||0} color={p.progress>=100?'#4ade80':'#C8FF00'}/>
                        <Badge label={ps.label} color={ps.color} bg={ps.bg}/>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {websites.length===0 && projects.length===0 && invoices.length===0 && (
              <div style={{ textAlign:'center', padding:'56px 20px', background:'#0d0d10', border:'1px solid rgba(255,255,255,0.05)', borderRadius:16 }}>
                <div style={{ fontSize:'3rem', marginBottom:16 }}>⚙️</div>
                <div style={{ fontWeight:800, fontSize:'1.1rem', marginBottom:8 }}>Votre espace est en cours de configuration</div>
                <p style={{ color:'#444', fontSize:'0.85rem', lineHeight:1.7, maxWidth:400, margin:'0 auto' }}>Vos projets, sites web et factures apparaîtront ici dès qu'ils seront ajoutés par l'équipe.</p>
              </div>
            )}
          </div>
        )}

        {/* ══ SITES ═══════════════════════════════════════════════════════════ */}
        {activeTab === 'sites' && (
          <div>
            {websites.length === 0
              ? <div style={{ textAlign:'center', padding:'48px', color:'#444', fontSize:'0.9rem' }}>Aucun site encore enregistré.</div>
              : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
                  {websites.map(w => {
                    const ws = WEB_STATUS[w.status] || WEB_STATUS.pause;
                    return (
                      <div key={w.id} style={{ background:'#0d0d10', border:`1px solid ${w.status==='en_test'?'rgba(96,165,250,0.2)':'rgba(255,255,255,0.05)'}`, borderRadius:14, padding:22 }}>
                        {/* Header */}
                        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14 }}>
                          <div>
                            <div style={{ fontWeight:800, fontSize:'1.05rem', marginBottom:4 }}>{w.name}</div>
                            {w.url && (
                              <a href={w.url.startsWith('http')?w.url:`https://${w.url}`} target="_blank" rel="noreferrer"
                                style={{ color:'#60a5fa', fontSize:'0.73rem', display:'inline-flex', alignItems:'center', gap:4 }}>
                                {w.url.replace(/^https?:\/\//,'')} ↗
                              </a>
                            )}
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:5, flexShrink:0 }}>
                            <span style={{ width:6, height:6, borderRadius:'50%', background:ws.dot, display:'inline-block' }}/>
                            <span style={{ fontSize:'0.66rem', color:ws.color, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>{ws.label}</span>
                          </div>
                        </div>
                        {/* Progress */}
                        <div style={{ marginBottom:14 }}>
                          <Bar value={w.progress||0} color={w.status==='en_ligne'?'#4ade80':'#C8FF00'}/>
                        </div>
                        {/* Meta */}
                        <div style={{ display:'flex', gap:16, marginBottom:14 }}>
                          {w.dueDate && <div><div style={{ fontSize:'0.6rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Échéance</div><div style={{ fontSize:'0.78rem', color:'#555' }}>{w.dueDate}</div></div>}
                          {w.techStack && <div><div style={{ fontSize:'0.6rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Stack</div><div style={{ fontSize:'0.78rem', color:'#555' }}>{w.techStack}</div></div>}
                        </div>
                        {/* Approbation */}
                        <ApprovalPanel website={w} clientEmail={currentUser.email} clientName={clientRecord.name}/>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </div>
        )}

        {/* ══ PROJETS ═════════════════════════════════════════════════════════ */}
        {activeTab === 'projects' && (
          <div>
            {projects.length === 0
              ? <div style={{ textAlign:'center', padding:'48px', color:'#444', fontSize:'0.9rem' }}>Aucun projet encore enregistré.</div>
              : (
                <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, overflow:'hidden' }}>
                  {projects.map((p, i) => {
                    const ps = PROJ_STATUS[p.status] || PROJ_STATUS.pending;
                    return (
                      <div key={p.id} style={{ padding:'20px 24px', borderBottom: i<projects.length-1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                          <span style={{ fontWeight:800, fontSize:'0.95rem' }}>{p.name}</span>
                          <Badge label={ps.label} color={ps.color} bg={ps.bg}/>
                        </div>
                        <Bar value={p.progress||0} color={p.progress>=100?'#4ade80':'#C8FF00'}/>
                        {p.dueDate && <div style={{ marginTop:8, fontSize:'0.72rem', color:'#333' }}>Échéance : {p.dueDate}</div>}
                      </div>
                    );
                  })}
                </div>
              )
            }
          </div>
        )}

        {/* ══ FACTURES ════════════════════════════════════════════════════════ */}
        {activeTab === 'invoices' && (
          <div>
            {invoices.length > 0 && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:24 }}>
                {[
                  { label:'Total facturé', val:`$${totalFacturé.toLocaleString()}`, color:'#EAEAEA' },
                  { label:'Payé',          val:`$${totalPayé.toLocaleString()}`,    color:'#4ade80' },
                  { label:'En attente',    val:`$${totalAttente.toLocaleString()}`, color:'#fbbf24' },
                ].map(k => (
                  <div key={k.label} style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.05)', borderRadius:10, padding:'14px 18px' }}>
                    <div style={{ fontSize:'0.6rem', color:'#333', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:8 }}>{k.label}</div>
                    <div style={{ fontSize:'1.6rem', fontWeight:900, color:k.color, letterSpacing:'-0.04em', fontVariantNumeric:'tabular-nums' }}>{k.val}</div>
                  </div>
                ))}
              </div>
            )}
            {invoices.length === 0
              ? <div style={{ textAlign:'center', padding:'48px', color:'#444', fontSize:'0.9rem' }}>Aucune facture pour le moment.</div>
              : (
                <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, overflow:'hidden' }}>
                  {invoices.map((inv, i) => {
                    const is = INVOICE_STATUS[inv.status] || { label:inv.status, color:'#555' };
                    return (
                      <div key={inv.id} style={{ padding:'16px 22px', borderBottom: i<invoices.length-1 ? '1px solid rgba(255,255,255,0.04)' : 'none', display:'flex', alignItems:'center', gap:16 }}>
                        <div style={{ fontWeight:700, color:'#444', fontSize:'0.72rem', minWidth:90, fontVariantNumeric:'tabular-nums' }}>{inv.number}</div>
                        <div style={{ flex:1 }}>
                          <div style={{ color:'#888', fontSize:'0.85rem' }}>{inv.description || 'Services professionnels'}</div>
                          {inv.dueDate && <div style={{ fontSize:'0.7rem', color:'#333', marginTop:2 }}>{inv.dueDate}</div>}
                        </div>
                        <div style={{ fontWeight:800, fontSize:'0.95rem', fontVariantNumeric:'tabular-nums' }}>${(inv.amount||0).toLocaleString()}</div>
                        <Badge label={is.label} color={is.color} bg={is.bg}/>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </div>
        )}

        {/* ══ MESSAGES ════════════════════════════════════════════════════════ */}
        {activeTab === 'messages' && (
          <div style={{ maxWidth:560 }}>
            <SectionLabel>Envoyer un message à l'équipe</SectionLabel>
            {msgSent ? (
              <div style={{ background:'rgba(74,222,128,0.07)', border:'1px solid rgba(74,222,128,0.15)', borderRadius:12, padding:'28px 24px', textAlign:'center' }}>
                <div style={{ fontSize:'2rem', marginBottom:10 }}>✅</div>
                <div style={{ color:'#4ade80', fontWeight:800, marginBottom:6 }}>Message envoyé !</div>
                <div style={{ color:'#444', fontSize:'0.84rem', marginBottom:18 }}>Nous vous répondrons dans les plus brefs délais.</div>
                <button onClick={()=>setMsgSent(false)}
                  style={{ background:'none', border:'1px solid rgba(255,255,255,0.08)', color:'#555', padding:'8px 18px', borderRadius:8, cursor:'pointer', fontSize:'0.82rem' }}>
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <div style={{ background:'#0d0d10', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:24 }}>
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:'block', fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Sujet</label>
                  <input style={inp} placeholder="Question sur mon projet…" value={msgForm.subject} onChange={e=>setMsgForm({...msgForm,subject:e.target.value})}/>
                </div>
                <div style={{ marginBottom:18 }}>
                  <label style={{ display:'block', fontSize:'0.65rem', color:'#444', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Message</label>
                  <textarea style={{ ...inp, resize:'vertical', minHeight:110 }} placeholder="Décrivez votre demande…" value={msgForm.body} onChange={e=>setMsgForm({...msgForm,body:e.target.value})}/>
                </div>
                <button onClick={sendMessage} disabled={sending||!msgForm.subject||!msgForm.body}
                  style={{ background: (!msgForm.subject||!msgForm.body) ? 'rgba(255,255,255,0.06)' : '#C8FF00', color: (!msgForm.subject||!msgForm.body) ? '#333' : '#000', border:'none', padding:'10px 24px', borderRadius:8, fontWeight:800, fontSize:'0.85rem', cursor: (!msgForm.subject||!msgForm.body) ? 'not-allowed' : 'pointer', transition:'all 0.2s' }}>
                  {sending ? 'Envoi…' : 'Envoyer →'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`@keyframes pulse{0%,100%{opacity:0.5}50%{opacity:1}}`}</style>
    </div>
  );
}
